import React, { useState } from "react";
import { t } from "i18next";

import AssessmentLayout from "@/components/Layout/Assessment";
import ContentBar from "@/components/ContentBar";
import { EnvelopeIcon, PhoneIcon, ChatBubbleLeftEllipsisIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { postContact } from "@/api/contact";
import { useMutation } from "react-query";

const HelpCenterPage = () => {
  const [fullname, setFullname] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");

  const { mutateAsync, isLoading } = useMutation(postContact);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log(fullname, email, phone, message);
    if (!fullname || !email || !phone || !message) {
      toast.error("Please fill all fields");
    } else {
      mutateAsync({ name: fullname, email: email, phone_number: phone, message: message }).then(
        (res) => {
          console.log(res);
          toast.success("Message sent successfully");
          setFullname("");
          setEmail("");
          setPhone("");
          setMessage("");
        }
      );
    }
  };

  return (
    <div>
      <AssessmentLayout>
        <Toaster />
        <ContentBar title={t("helpCenter")} />

        <div className="bg-[#E4E3F2] w-full h-56 flex flex-col justify-center items-center gap-6 mt-8">
          <div className="text-4xl font-extrabold">{t("How can we help?")}</div>
          <div className="flex justify-center gap-2 flex-col"></div>
        </div>

        <div className="bg-white flex flex-col md:flex-row justify-center px-5 pt-24 pb-12 gap-12 md:gap-24">
          <div className="w-full md:w-[344px] border rounded-lg shadow-md overflow-visible">
            <Link to="/help-center/faq">
              <div className="flex flex-col items-center gap-6 p-4 text-center">
                <div>
                  <ChatBubbleLeftEllipsisIcon className="h-12 w-12 font-medium text-gray-600" />
                </div>
                <div className="text-3xl font-extrabold">FAQs</div>
                <div className="text-gray-500 text-lg">
                  {t(
                    "Everything you need to know about the product. Can’t find the answer you’re looking for? Please contact us."
                  )}
                </div>
              </div>
            </Link>
          </div>
          <div className="w-full md:w-[344px] border rounded-lg shadow-md overflow-visible">
            <Link to="/help-center/how-to">
              <div className="flex flex-col items-center gap-6 p-4 text-center">
                <div>
                  <ChatBubbleLeftEllipsisIcon className="h-12 w-12 font-medium text-gray-600" />
                </div>
                <div className="text-3xl font-extrabold">{t("howTo")}</div>
                <div className="text-gray-500 text-lg">
                  {t(
                    "Everything you need to know about the how to finish a task. Can’t find the answer you’re looking for? Please contact us."
                  )}
                </div>
              </div>
            </Link>
          </div>
        </div>

        <div className="relative bg-white flex justify-center">
          <div className="absolute inset-0">
            <div className="absolute inset-y-0 left-0 w-1/2 bg-white" />
          </div>
          <div className="relative mx-auto max-w-7xl lg:grid lg:grid-cols-5">
            <div className="bg-white py-16 px-4 sm:px-6 lg:col-span-2 lg:px-8  xl:pr-12">
              <div className="mx-auto max-w-lg">
                <h2 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
                  {t("contactUs")}
                </h2>
                <p className="mt-3 text-lg leading-6 text-gray-500">
                  {t(
                    "Can’t find your answer you’re looking for? Our friendly team would love to hear from you!"
                  )}
                </p>
                <dl className="mt-8 text-base text-gray-500">
                  <div className="mt-6">
                    <dt className="sr-only">Phone number</dt>
                    <dd className="flex">
                      <PhoneIcon
                        className="h-6 w-6 flex-shrink-0 text-gray-400"
                        aria-hidden="true"
                      />
                      <span className="ml-3">+90 (850) 360 1288</span>
                    </dd>
                  </div>
                  <div className="mt-3">
                    <dt className="sr-only">Email</dt>
                    <dd className="flex">
                      <EnvelopeIcon
                        className="h-6 w-6 flex-shrink-0 text-gray-400"
                        aria-hidden="true"
                      />
                      <span className="ml-3">hey@mikrolo.com</span>
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
            <div className="bg-white py-16 px-4 sm:px-6 lg:col-span-3  lg:px-8 xl:pl-12">
              <div className="mx-auto max-w-lg lg:max-w-none">
                <form
                  action="#"
                  method="POST"
                  className="grid grid-cols-1 gap-y-6"
                  onSubmit={handleSubmit}>
                  <div>
                    <label htmlFor="full-name" className="sr-only">
                      Full name
                    </label>
                    <input
                      onChange={(e) => setFullname(e.target.value)}
                      type="text"
                      name="full-name"
                      id="full-name"
                      autoComplete="name"
                      className="outline-none block w-full rounded-md border-gray-300 py-3 px-4 placeholder-gray-500 shadow-sm  border focus:border-primaryColor"
                      placeholder={t("fullName")}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="sr-only">
                      Email
                    </label>
                    <input
                      onChange={(e) => setEmail(e.target.value)}
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      className="outline-none block w-full rounded-md border-gray-300 py-3 px-4 placeholder-gray-500 shadow-sm  border focus:border-primaryColor"
                      placeholder={t("email")}
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="sr-only">
                      Phone
                    </label>
                    <input
                      onChange={(e) => setPhone(e.target.value)}
                      type="text"
                      name="phone"
                      id="phone"
                      autoComplete="tel"
                      className="outline-none block w-full rounded-md border-gray-300 py-3 px-4 placeholder-gray-500 shadow-sm  border focus:border-primaryColor"
                      placeholder={t("phone")}
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="sr-only">
                      Message
                    </label>
                    <textarea
                      onChange={(e) => setMessage(e.target.value)}
                      id="message"
                      name="message"
                      rows={4}
                      className="outline-none block w-full rounded-md border-gray-300 py-3 px-4 placeholder-gray-500 shadow-sm border focus:border-primaryColor"
                      placeholder={t("yourMessage")}
                      defaultValue={""}
                    />
                  </div>
                  <div>
                    <button
                      disabled={isLoading}
                      type="submit"
                      className="inline-flex justify-center rounded-md border border-transparent bg-primaryColor py-3 px-6 text-base font-medium text-white shadow-sm hover:bg-primaryHoverColorfocus:outline-none ">
                      {isLoading ? (
                        <svg
                          role="status"
                          className="inline w-5 h-5 text-white animate-spin"
                          viewBox="0 0 100 101"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                            fill="#E5E7EB"
                          />
                          <path
                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                            fill="currentColor"
                          />
                        </svg>
                      ) : (
                        t("submit")
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </AssessmentLayout>
    </div>
  );
};

export default HelpCenterPage;
